/**
 * OperationHistoryDateRangeFilter Component
 *
 * Generic date range filter with two DateInput fields.
 * Uses dayjs for locale-aware date parsing.
 */

import { memo, useCallback, useMemo } from 'react';

import { Group } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'react-i18next';

import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';

import styles from './OperationHistoryDateRangeFilter.module.css';

dayjs.extend(customParseFormat);

interface OperationHistoryDateRangeFilterProps {
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (date: Date | string | null) => void;
  onDateToChange: (date: Date | string | null) => void;
}

export const OperationHistoryDateRangeFilter = memo(
  function OperationHistoryDateRangeFilter({
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
  }: OperationHistoryDateRangeFilterProps) {
    const { t, i18n } = useTranslation();

    const dateFormat = useMemo(() => {
      return i18n.language === 'pl' ? 'DD.MM.YYYY' : 'MM/DD/YYYY';
    }, [i18n.language]);

    const parseDateString = useCallback(
      (value: string): Date | null => {
        if (!value) return null;

        const formats =
          i18n.language === 'pl'
            ? ['DD.MM.YYYY', 'D.M.YYYY', 'DD.MM.YY']
            : ['MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY'];

        for (const format of formats) {
          const parsed = dayjs(value, format, true);
          if (parsed.isValid()) {
            return parsed.toDate();
          }
        }

        return null;
      },
      [i18n.language]
    );

    return (
      <Group grow>
        <DateInput
          label={t('history.from')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          dateParser={parseDateString}
          value={dateFrom}
          onChange={onDateFromChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          maxDate={dateTo || undefined}
          className={styles.dateInput}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />
        <DateInput
          label={t('history.to')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          dateParser={parseDateString}
          value={dateTo}
          onChange={onDateToChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          minDate={dateFrom || undefined}
          className={styles.dateInput}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />
      </Group>
    );
  }
);
